// Tenantra Inc. - Profile Page
import { useEffect, useState, useRef } from 'react';
import { Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { TableSkeleton } from '@/components/dashboard/TableKit';
import api, { apiHelpers } from '@/services/api';
import { getCurrentUser } from '@/services/auth';
import { useLoadScript } from '@react-google-maps/api';
import usePlacesAutocomplete, { getDetails } from 'use-places-autocomplete';

const GOOGLE_MAPS_LIBRARIES = ['places'];

const PROVINCE_CODES = {
  'alberta': 'AB', 'british columbia': 'BC', 'manitoba': 'MB', 'new brunswick': 'NB',
  'newfoundland and labrador': 'NL', 'nova scotia': 'NS', 'northwest territories': 'NT',
  'nunavut': 'NU', 'ontario': 'ON', 'prince edward island': 'PE', 'quebec': 'QC',
  'saskatchewan': 'SK', 'yukon': 'YT',
  'ab': 'AB', 'bc': 'BC', 'mb': 'MB', 'nb': 'NB', 'nl': 'NL', 'ns': 'NS',
  'nt': 'NT', 'nu': 'NU', 'on': 'ON', 'pe': 'PE', 'qc': 'QC', 'sk': 'SK', 'yt': 'YT'
};

function ProfileAddressInput({ value, onChange, onSelect, className }) {
  const { ready, suggestions: { status, data }, setValue: setAC, clearSuggestions } = usePlacesAutocomplete({
    requestOptions: { componentRestrictions: { country: 'ca' } }, debounce: 250
  });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleInput = (e) => { onChange(e.target.value); setAC(e.target.value); setOpen(true); };

  const handlePick = async (s) => {
    setOpen(false); clearSuggestions();
    onChange(s.structured_formatting.main_text);
    try {
      const det = await getDetails({ placeId: s.place_id, fields: ['address_components'] });
      if (det?.address_components) {
        const r = { street: '', city: '', province: '', postal_code: '' };
        let num = '', route = '';
        for (const c of det.address_components) {
          if (c.types.includes('street_number')) num = c.long_name;
          if (c.types.includes('route')) route = c.long_name;
          if (c.types.includes('locality') || c.types.includes('sublocality_level_1')) r.city = c.long_name;
          if (c.types.includes('administrative_area_level_1')) r.province = PROVINCE_CODES[c.long_name.toLowerCase()] || PROVINCE_CODES[c.short_name.toLowerCase()] || c.short_name;
          if (c.types.includes('postal_code')) r.postal_code = c.long_name;
        }
        r.street = num ? `${num} ${route}` : route;
        onSelect(r);
      }
    } catch (err) { console.error('Place details error:', err); }
  };

  return (
    <div ref={ref} className="relative">
      <Input value={value} onChange={handleInput} onFocus={() => { if (data.length) setOpen(true); }}
        disabled={!ready} autoComplete="off" placeholder="Address"
        className={className} data-testid="profile-street-input" />
      {open && status === 'OK' && data.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {data.map((s) => (
            <li key={s.place_id} onClick={() => handlePick(s)}
              className="px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0">
              <span className="font-medium">{s.structured_formatting.main_text}</span>
              <span className="text-slate-400 text-xs ml-1">{s.structured_formatting.secondary_text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ProfilePage() {
  const { isLoaded } = useLoadScript({ googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY, libraries: GOOGLE_MAPS_LIBRARIES });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const [profile, setProfile] = useState({
    email: '', full_name: '', phone: '', company_name: '',
    address: '', city: '', province: '', postal_code: '', created_at: ''
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: '', new_password: '', confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/me');
      const user = getCurrentUser();
      const d = response.data;
      const l = d.landlord || {};
      setProfile({
        email: d.email || user?.email || '',
        full_name: l.legal_name || d.full_name || d.name || '',
        phone: d.phone || l.phone || '',
        company_name: l.business_name || d.company_name || '',
        address: l.address || d.address || '',
        city: l.city || d.city || '',
        province: l.province || d.province || '',
        postal_code: l.postal_code || d.postal_code || '',
        created_at: d.created_at || ''
      });
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  const set = (field) => (e) => setProfile(prev => ({ ...prev, [field]: e.target.value }));

  const handleAddressSelect = (parsed) => {
    setProfile(prev => ({
      ...prev,
      address: parsed.street || prev.address,
      city: parsed.city || prev.city,
      province: parsed.province || prev.province,
      postal_code: parsed.postal_code || prev.postal_code,
    }));
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      await api.put('/auth/profile', {
        full_name: profile.full_name, phone: profile.phone,
        company_name: profile.company_name, address: profile.address,
        city: profile.city, province: profile.province, postal_code: profile.postal_code
      });
      toast.success('Profile updated');
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordField = (field) => (e) => {
    setPasswordForm(prev => ({ ...prev, [field]: e.target.value }));
    if (passwordErrors[field]) setPasswordErrors(prev => ({ ...prev, [field]: null }));
  };

  const validatePassword = () => {
    const errors = {};
    if (!passwordForm.current_password) errors.current_password = 'Required';
    if (!passwordForm.new_password) errors.new_password = 'Required';
    else if (passwordForm.new_password.length < 8) errors.new_password = 'Min 8 characters';
    if (passwordForm.new_password !== passwordForm.confirm_password) errors.confirm_password = 'Does not match';
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChangePassword = async () => {
    if (!validatePassword()) return;
    try {
      setChangingPassword(true);
      await api.post('/auth/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed');
      setShowPasswordForm(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setChangingPassword(false);
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '--';

  if (loading) {
    return <TableSkeleton rows={5} spans={[5, 3, 2, 2]} />;
  }

  const inputCls = "h-8 text-xs border-slate-200 rounded-md";
  const labelCls = "text-[11px] text-slate-500 mb-0.5 block";
  const sectionCls = "text-[10px] font-semibold text-slate-400 uppercase tracking-widest";

  return (
    <div className="max-w-xl mx-auto px-3 sm:px-0 pb-8" data-testid="profile-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-sm font-semibold text-slate-900" data-testid="profile-title">Profile</h1>
          <p className="text-[11px] text-slate-400">Manage your account</p>
        </div>
        <span className="text-[10px] text-slate-400">Since {fmtDate(profile.created_at)}</span>
      </div>

      {/* Account row */}
      <div className="flex items-center gap-2.5 border border-slate-200 rounded-md px-3 py-2.5 mb-3" data-testid="account-card">
        <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
          {profile.full_name?.charAt(0)?.toUpperCase() || profile.email?.charAt(0)?.toUpperCase() || 'U'}
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-slate-900 truncate">{profile.full_name || 'No name set'}</p>
          <p className="text-[10px] text-slate-400 truncate">{profile.email}</p>
        </div>
      </div>

      {/* Personal Information */}
      <div className="border border-slate-200 rounded-md mb-3" data-testid="personal-info-section">
        <div className="px-3 py-2 border-b border-slate-100">
          <span className={sectionCls}>Personal Information</span>
        </div>
        <div className="p-3 space-y-2.5">
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <label className={labelCls}>Full Name</label>
              <Input value={profile.full_name} onChange={set('full_name')} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <Input value={profile.phone} onChange={set('phone')} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Company Name</label>
            <Input value={profile.company_name} onChange={set('company_name')} className={inputCls} />
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="border border-slate-200 rounded-md mb-3" data-testid="address-section">
        <div className="px-3 py-2 border-b border-slate-100">
          <span className={sectionCls}>Address</span>
        </div>
        <div className="p-3 space-y-2.5">
          <div>
            <label className={labelCls}>Street</label>
            {isLoaded ? (
              <ProfileAddressInput
                value={profile.address}
                onChange={(val) => setProfile(prev => ({ ...prev, address: val }))}
                onSelect={handleAddressSelect}
                className={inputCls}
              />
            ) : (
              <Input value={profile.address} onChange={set('address')} className={inputCls} />
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className={labelCls}>City</label>
              <Input value={profile.city} onChange={set('city')} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Province</label>
              <Input value={profile.province} onChange={set('province')} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Postal</label>
              <Input value={profile.postal_code} onChange={set('postal_code')} className={inputCls} />
            </div>
          </div>
        </div>
      </div>

      {/* Save */}
      <div className="flex justify-end mb-3">
        <Button onClick={handleSaveProfile} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white text-[11px] font-medium h-8 px-4" data-testid="save-profile-btn">
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Password */}
      <div className="border border-slate-200 rounded-md" data-testid="password-section">
        <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
          <span className={sectionCls}>Password</span>
          {!showPasswordForm && (
            <button onClick={() => setShowPasswordForm(true)} className="text-[11px] text-teal-600 font-medium" data-testid="change-password-toggle">
              Change
            </button>
          )}
        </div>

        {showPasswordForm ? (
          <div className="p-3 space-y-2.5">
            <div>
              <label className={labelCls}>Current Password</label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordForm.current_password}
                  onChange={handlePasswordField('current_password')}
                  className={`${inputCls} pr-8 ${passwordErrors.current_password ? 'border-red-400' : ''}`}
                  data-testid="current-password-input"
                />
                <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
                  {showCurrentPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                </button>
              </div>
              {passwordErrors.current_password && <p className="text-red-500 text-[10px] mt-0.5">{passwordErrors.current_password}</p>}
            </div>
            <div>
              <label className={labelCls}>New Password</label>
              <div className="relative">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.new_password}
                  onChange={handlePasswordField('new_password')}
                  className={`${inputCls} pr-8 ${passwordErrors.new_password ? 'border-red-400' : ''}`}
                  data-testid="new-password-input"
                />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
                  {showNewPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                </button>
              </div>
              {passwordErrors.new_password && <p className="text-red-500 text-[10px] mt-0.5">{passwordErrors.new_password}</p>}
            </div>
            <div>
              <label className={labelCls}>Confirm Password</label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={handlePasswordField('confirm_password')}
                className={`${inputCls} ${passwordErrors.confirm_password ? 'border-red-400' : ''}`}
                data-testid="confirm-password-input"
              />
              {passwordErrors.confirm_password && <p className="text-red-500 text-[10px] mt-0.5">{passwordErrors.confirm_password}</p>}
            </div>
            <div className="flex items-center justify-between pt-1">
              <button onClick={() => { setShowPasswordForm(false); setPasswordForm({ current_password: '', new_password: '', confirm_password: '' }); setPasswordErrors({}); }} className="text-[11px] text-slate-400 font-medium" data-testid="cancel-password-btn">
                Cancel
              </button>
              <Button onClick={handleChangePassword} disabled={changingPassword} className="bg-teal-600 hover:bg-teal-700 text-white text-[11px] font-medium h-8 px-4" data-testid="submit-password-btn">
                {changingPassword ? 'Changing...' : 'Update Password'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="px-3 py-2.5 flex items-center gap-2.5">
            <Lock className="w-3.5 h-3.5 text-slate-300" />
            <span className="text-xs text-slate-600 flex-1">••••••••</span>
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
          </div>
        )}
      </div>
    </div>
  );
}
